import { useCallback, useEffect, useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { StateService } from './services/stateService';
import type { AppSettings, FormSchema } from './types';
import { DisplayService } from './services/displayService';
import { ValidationService } from './services/validationService';
import { FormService } from './services/formService';
import { SavedDataLoader } from './services/savedDataLoader';
import { HttpService } from './services/httpService';
import { DataService } from './services/dataService';
import { DataSummarizer } from './services/dataSummarizer';
import { ScrollService } from './services/scrollService';
import { ModalService } from './services/modalService';
import { RecaptchaService } from './services/recaptchaService';
import { FieldInfoService } from './services/fieldInfoService';
import { NZBNService } from './services/nzbnService';
import { GuidService } from './services/guidService';
import { applyDemoPrefill, isDemoPrefillRequested } from './services/demoPrefill';
import { FormProvider, type FormContextValue } from './context/FormContext';
import { StepForm } from './components/StepForm';
import { isGroupSetSchema } from './types';

export interface StepFormAppProps {
  settings: AppSettings;
  schema: FormSchema;
  validationService?: InstanceType<typeof ValidationService>;
  displayService?: InstanceType<typeof DisplayService>;
  ValidationServiceClass?: typeof ValidationService;
  DisplayServiceClass?: typeof DisplayService;
  customStepComponents?: Record<string, ComponentType<{ stepSchema: FormSchema['steps'][number] }>>;
  StateServiceClass?: typeof StateService;
  children?: ReactNode;
  afterForm?: ReactNode;
}

export function StepFormApp({
  settings,
  schema,
  validationService: customValidationService,
  displayService: customDisplayService,
  ValidationServiceClass,
  DisplayServiceClass,
  customStepComponents,
  StateServiceClass,
  children,
  afterForm,
}: StepFormAppProps) {
  const [, setTick] = useState<number>(0);
  const forceUpdate = useCallback<() => void>(() => setTick((n) => n + 1), []);

  const services = useMemo(() => {
    const stateService = new (StateServiceClass ?? StateService)();
    const savedDataLoader = new SavedDataLoader(settings.formData);
    const formService = new FormService(settings.guid || '', savedDataLoader, stateService);
    const displayService =
      customDisplayService ??
      new (DisplayServiceClass ?? DisplayService)(formService);
    const validationService =
      customValidationService ??
      new (ValidationServiceClass ?? ValidationService)(formService);
    const httpService = new HttpService();
    const dataService = new DataService(httpService, settings.apiBaseUrl, settings.csrfToken || '');
    const dataSummarizer = new DataSummarizer(formService, displayService, validationService);
    const scrollService = new ScrollService();
    const modalService = new ModalService();
    const recaptchaService = new RecaptchaService(settings.captchaKey || '');
    const fieldInfoService = new FieldInfoService(formService);
    const nzbnService = new NZBNService(
      httpService,
      settings.nzbnApiUrl || '',
      settings.nzbnApiToken || ''
    );
    const guidService = new GuidService();

    const groupSetCounts: Record<string, number> = {};

    schema.steps.forEach((step) => {
      step.sections.forEach((section) => {
        section.groups?.forEach((groupOrSet) => {
          if (isGroupSetSchema(groupOrSet)) {
            groupSetCounts[groupOrSet.groupSetId] = savedDataLoader.getNumOfGroups(
              groupOrSet.group.id,
              groupOrSet.min ?? 1
            );
          }
        });
      });
    });

    formService.initializeFromSchema(schema, groupSetCounts);

    if (settings.demoPrefillEnabled && isDemoPrefillRequested()) {
      applyDemoPrefill(formService, schema);
    }

    const activeStep = formService.getFormData().steps[0];
    if (activeStep && !formService.getActiveStep()) {
      formService.setActiveStep(activeStep);
    }

    if (settings.submitResponse?.success) {
      stateService.formSubmitted = true;
      stateService.showForm = false;
    }

    return {
      stateService,
      formService,
      displayService,
      validationService,
      httpService,
      dataService,
      dataSummarizer,
      scrollService,
      modalService,
      recaptchaService,
      fieldInfoService,
      nzbnService,
      guidService,
      groupSetCounts,
    };
  }, [settings, schema, customValidationService, customDisplayService, ValidationServiceClass, DisplayServiceClass, StateServiceClass]);

  const [groupSetCounts, setGroupSetCountsState] = useState<Record<string, number>>(
    services.groupSetCounts
  );

  const setGroupSetCount = useCallback<(groupSetId: string, count: number) => void>((groupSetId: string, count: number) => {
    setGroupSetCountsState((prev) => ({ ...prev, [groupSetId]: count }));
  }, []);

  const contextValue: FormContextValue = {
    settings,
    schema,
    form: services.formService.getFormData(),
    formService: services.formService,
    stateService: services.stateService,
    displayService: services.displayService,
    validationService: services.validationService,
    dataService: services.dataService,
    dataSummarizer: services.dataSummarizer,
    scrollService: services.scrollService,
    modalService: services.modalService,
    recaptchaService: services.recaptchaService,
    fieldInfoService: services.fieldInfoService,
    nzbnService: services.nzbnService,
    httpService: services.httpService,
    guidService: services.guidService,
    groupSetCounts,
    setGroupSetCount,
    forceUpdate,
  };

  useEffect(() => {
    if (settings.submitResponse && !settings.submitResponse.success) {
      const timer = setTimeout(() => {
        services.modalService.open('submit-error');
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [settings.submitResponse, services.modalService]);

  return (
    <FormProvider value={contextValue}>
      <StepForm schema={schema} customStepComponents={customStepComponents}>
        {children}
      </StepForm>
      {afterForm}
    </FormProvider>
  );
}
